import CryptoJS from 'crypto-js';
import { chunk, uniqBy } from 'lodash-es';
import getMime from 'name2mime';

import { isDesktop } from '@carboncopyco/mimesis-shared';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import type { Tour } from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { FILE_SERVICE_APIS } from './constants';

interface UploadedFileHashProps {
  resource: any;
  organizationId: string;
  tourId: Tour['id'];
  fileServiceDynamicLink: string;
}

interface UploadToursResource {
  organizationId: string;
  tourId: Tour['id'];
  resourceId: string;
  token: string;
  fileData: ArrayBuffer;
}

interface UploadAllResourcesProps {
  resources: any;
  tourId: number;
  fileServiceToken: string;
  organizationId: string;
  uploadToursResource: ({
    organizationId,
    tourId,
    resourceId,
    token,
    fileData,
  }: UploadToursResource) => any;
  uploadedFilesSet: Set<string>;
  addToUploadedFilesSet: (fileName: string) => void;
  removeFromUploadedFilesSet: (fileName: string) => void;
  onProgress?: (progress: number) => void;
}

const debug = Debug.extend('utils/resources');
const sdkLogger = scopedLogger(Scope.MIMESIS_SDK);

const CLIENTS_TO_APPLY_BASE64_FIX = ['zscaler'];
const FILE_TYPE_TO_APPLY_BASE64_FIX = ['woff2'];

export const findAllLinksInJson = ({
  events,
  capturing,
}: {
  events: eventWithTime[];
  capturing: boolean;
}) => {
  const urlRegex: RegExp =
    /\b(?:blob:)?https?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_.|]/gi;
  const capturingModifiers = [
    'demosgy',
    'demosgx',
    'outdemo.io',
    'demostack.com/files',
  ];
  const replayingModifiers = ['demostack.com/files'];
  const regexResult = JSON.stringify(events).match(urlRegex);
  const shouldCaptureUrl = (regexResult: string) => {
    if (isDesktop) {
      return true;
    }

    return capturingModifiers.some((value) => regexResult.includes(value));
  };
  const allLinksInJson: Set<string> = new Set([]);
  for (const result of regexResult || []) {
    const resultIncludesModifier = capturing
      ? shouldCaptureUrl(result)
      : replayingModifiers.some((value) => result.includes(value));
    if (resultIncludesModifier) {
      allLinksInJson.add(result);
    }
  }

  return Array.from(allLinksInJson).sort((a, b) => b.length - a.length);
};

const isFileService = (url: string) =>
  FILE_SERVICE_APIS.some((api) => url.includes(api));
const shouldAddCredentials = (url: string, isRedirected?: boolean) => {
  return !isRedirected && !isFileService(url);
};

const tryDownloadResource = (
  originalUrl: string,
  isRedirected?: boolean
): Promise<any> => {
  return new Promise<any>((resolve, reject) => {
    const downloadAsync = async () => {
      try {
        const url = originalUrl.replace('http://', 'https://');
        const includeCredentials = shouldAddCredentials(url, isRedirected);
        const response = await fetch(url, {
          ...(includeCredentials
            ? {
                credentials: 'include',
                headers: { 'x-fs-response-mode': 'url' },
              }
            : {}),
        });
        if (!response.ok) {
          return reject('Response not OK');
        }

        const responseHeaders = Object.fromEntries(response.headers.entries());

        if (responseHeaders['content-type'] === 'application/json') {
          const responseJson = await response.json();
          if (
            responseJson['x-fs-response-mode'] === 'url' &&
            responseJson.url
          ) {
            return tryDownloadResource(responseJson.url, true)
              .then((response) => resolve(response))
              .catch((error) => reject(error));
          }
        }

        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer?.byteLength === 0) {
          return reject({
            url: originalUrl,
            message: 'File is empty',
          });
        }
        const contentType =
          responseHeaders['content-type'] ?? getMime(url).type;
        const isImage =
          contentType.includes('image/') || contentType.includes('text/plain');
        const isCss = contentType.includes('text/css');
        const isFont =
          contentType.includes('font') ||
          contentType.includes('woff') ||
          contentType.includes('ttf');
        const isOctetStream = contentType.includes('application/octet-stream');
        const isBinaryOctetStream = contentType.includes('binary/octet-stream');
        if (
          !(isImage || isCss || isFont || isOctetStream || isBinaryOctetStream)
        ) {
          return reject({
            url: originalUrl,
            message: 'This file is not supported',
          });
        }

        return resolve({
          contentType,
          arrayBuffer,
        });
      } catch (error) {
        reject({
          url: originalUrl,
          message: 'Failed downloading the resource',
          error,
        });
      }
    };

    downloadAsync().catch((error) => {
      reject(error);
    });
  });
};

const getFileHash = (originalUrl: string) => {
  /*
    Splitting originalUrl by origin gives us everything after "/" ensuring we
    will have the same resourceId for the same file
  */
  const encodedUrl = new URL(originalUrl);
  const { origin } = encodedUrl;
  const urlWithoutEncodedHostname = originalUrl.split(origin)[1];
  return CryptoJS.MD5(urlWithoutEncodedHostname).toString();
};

export const downloadAllResources = (allLinksInJson: string[]) => {
  const promises: Promise<unknown>[] = [];
  for (const link of allLinksInJson || []) {
    if (link.startsWith('blob:')) {
      const promise = new Promise((resolve, _) => {
        resolve({
          fileData: null,
          resourceId: null,
          originalUrl: link,
        });
      });
      promises.push(promise);
    } else {
      const promise = tryDownloadResource(link)
        .then((downloadedFile) => {
          if (!downloadedFile) {
            return;
          }
          debug('downloadAllResources', { downloadedFile });
          return {
            fileData: downloadedFile,
            originalUrl: link,
            resourceId: getFileHash(link),
          };
        })
        .catch((error) => {
          sdkLogger.error('Error downloading resource', { link }, error);
          debug('downloadAllResources', { error });
          /*
            If file failed localising, replace the outdemo.io link with an empty
            string ensuring there is not 406 expired session errors from the cloner
          */
          return {
            fileData: null,
            originalUrl: link,
            resourceId: '',
          };
        });
      promises.push(promise);
    }
  }

  debug('downloadAllResources/Promise.all', { promises });

  return Promise.all(promises)
    .then((responses) => {
      debug('downloadAllResources/Promise.all', {
        responses,
      });
      return responses.filter((response) => response);
    })
    .catch((error) => {
      debug('downloadAllResources/Promise.all', { error });
      sdkLogger.error('Error downloading resources', { allLinksInJson }, error);
    });
};

export const getDownloadableResourcesArray = ({
  events,
}: {
  events: eventWithTime[];
}) => {
  const allLinksInJson = findAllLinksInJson({
    events,
    capturing: true,
  });

  return downloadAllResources(allLinksInJson)
    .then((downloadedResources) => {
      if (!downloadedResources) {
        return null;
      }
      return uniqBy(downloadedResources, 'originalUrl');
    })
    .catch((error) => {
      debug('localizeResources/downloadAllResources', {
        error,
      });
      sdkLogger.error('Error downloading resources', { events }, error);
      return null;
    });
};

const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const uploadedFileLinkGen = ({
  resource,
  organizationId,
  tourId,
  fileServiceDynamicLink,
}: UploadedFileHashProps) => {
  if (!resource.resourceId || resource.resourceId === '') {
    /*
      If file failed localising, replace the outdemo.io link with an empty
      string ensuring there is not 406 expired session errors from the cloner
    */
    return '';
  }

  if (
    CLIENTS_TO_APPLY_BASE64_FIX.some((client) =>
      resource.originalUrl.includes(client)
    )
  ) {
    if (
      FILE_TYPE_TO_APPLY_BASE64_FIX.some((fileType) =>
        resource.fileData.contentType.includes(fileType)
      )
    ) {
      const base64String = arrayBufferToBase64(resource.fileData.arrayBuffer);
      return `data:${resource.fileData.contentType};charset=utf-8;base64,${base64String}`;
    }
  }
  return `${fileServiceDynamicLink ?? ''}/files/tours/${organizationId}/tour/${tourId}/resource/${
    resource.resourceId
  }`;
};

const getBase64FromBlobSet = (
  blobFilesToBeReplaced: Set<{
    base64: string;
    blobURI: string;
  }>,
  originalUrl: string
) => {
  for (const blobObject of blobFilesToBeReplaced) {
    if (blobObject.blobURI === originalUrl) {
      const BASE64_URI_CLEANUP = /(\r\n|\n|\r)/gm;
      return blobObject.base64.replace(BASE64_URI_CLEANUP, '');
    }
  }
  return;
};

export const replaceLinksInTourJson = ({
  events,
  resources,
  organizationId,
  tourId,
  blobFilesToBeReplaced,
  fileServiceDynamicLink,
}: {
  events: eventWithTime[];
  resources: any;
  organizationId: any;
  tourId: number;
  blobFilesToBeReplaced: Set<{
    base64: string;
    blobURI: string;
  }>;
  fileServiceDynamicLink: string;
}) => {
  let localizedEvents = JSON.stringify(events);
  if (!resources || resources.length === 0) {
    return localizedEvents;
  }
  const downloadResourcesLength = resources.length - 1;
  for (const [index, resource] of resources.entries() || []) {
    if (resource) {
      const base64FromBlob = getBase64FromBlobSet(
        blobFilesToBeReplaced,
        resource.originalUrl
      );
      if (base64FromBlob) {
        localizedEvents = localizedEvents.replaceAll(
          resource.originalUrl,
          base64FromBlob
        );
      } else {
        const replacementUrl = uploadedFileLinkGen({
          resource: resource,
          organizationId,
          tourId,
          fileServiceDynamicLink,
        });
        localizedEvents = localizedEvents.replaceAll(
          resource.originalUrl,
          replacementUrl
        );
      }
    }
    if (index === downloadResourcesLength) {
      return localizedEvents;
    }
  }
};

const RESOURCE_CHUNK_SIZE = 35;

export const uploadAllResources = async ({
  resources,
  tourId,
  fileServiceToken,
  organizationId,
  uploadToursResource,
  uploadedFilesSet,
  addToUploadedFilesSet,
  removeFromUploadedFilesSet,
  onProgress,
}: UploadAllResourcesProps) => {
  if (!Array.isArray(resources) || resources.length === 0) {
    return;
  }

  const resourceChunks = chunk(resources, RESOURCE_CHUNK_SIZE);
  const totalResources = resources.length;
  let uploadedResources = 0;

  for (const resourceChunk of resourceChunks) {
    const uploadPromises = [];

    for (const resource of resourceChunk) {
      if (resource?.resourceId && resource?.fileData) {
        if (!uploadedFilesSet.has(resource.resourceId)) {
          addToUploadedFilesSet(resource.resourceId);
          uploadPromises.push(
            uploadToursResource({
              organizationId,
              tourId,
              resourceId: resource.resourceId,
              token: fileServiceToken,
              fileData: resource.fileData,
            })
              .then(() => {
                uploadedResources++;
                onProgress?.(uploadedResources / totalResources);
              })
              .catch((error) => {
                removeFromUploadedFilesSet(resource.resourceId);
                debug('localizeResources/uploadAllResources', { error });
                sdkLogger.error(
                  'Error uploading resource',
                  { resource },
                  error
                );
              })
          );
        } else {
          debug(
            'localizeResources/uploadAllResources',
            `File ${resource.resourceId} already uploaded. Skipping...`
          );
        }
      }
    }

    await Promise.all(uploadPromises);
  }
};
